import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { DeactivateOneLinkModal } from '../../components';
import OneLinkManagerGridView from '../../components/OneLinksComponent/OneLinksManagerGridView/OneLinksManagerGridView';
import OneLinkManagerListView from '../../components/OneLinksComponent/OneLinksManagerListView/OneLinksManagerListView';
import { IPaginatedListChangeEvent, IPaginatedListRef } from '../../components/PaginatedList/PaginatedList';
import { PageHeader } from '../../components/_mui';
import { MAX_ONE_LINKS_PER_PAGE } from '../../constants/oneLink';
import { routes } from '../../constants/routing';
import { INITIAL_PAGE } from '../../constants/table';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import { disableOneLinkRequest, optimisticDeactivateOneLink } from '../../store/actions/oneLink';
import { fetchBusinessReasonsRequest } from '../../store/actions/reasons';
import { fetchUsersRequest } from '../../store/actions/users';
import { selectIsAdminTypeUser } from '../../store/selectors/auth';
import { selectBusinessReasons } from '../../store/selectors/reasons';
import { IOneLink } from '../../types/oneLink';
import { ViewTypesEnum } from '../../types/shell';
import { handleApiError } from '../../utils/ui';
import useFilters from './Filters/useFilters';

const OneLinksManagerContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { setHeader } = useHeaderDispatch();

  const [currentPageSize, setCurrentPageSize] = useState(MAX_ONE_LINKS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const [isOpenDeactivateDialog, setIsOpenDeactivateDialog] = useState(false);
  const [itemToDeactivate, setItemToDeactivate] = useState<IOneLink | null>(null);
  const [disabledLoaderIds, setDisabledLoaderIds] = useState<string[]>([]);
  const paginatedListRef = useRef<IPaginatedListRef<IOneLink>>(null);

  const isAdminTypeUser = useSelector(selectIsAdminTypeUser);
  const reasons = useSelector(selectBusinessReasons);

  const {
    filteredOneLinks,
    filters,
    oneLinkTotalCount,
    isOneLinksFetching,
    isFinalOneLink,
    filtersRef,
    Filters,
    fetchOneLinkSends,
    activeView,
  } = useFilters({ currentPageSize });

  const handleOpenDeactivateDialog = useCallback((item: IOneLink) => {
    setItemToDeactivate(item);
    setIsOpenDeactivateDialog(true);
  }, []);

  const handleCloseDeactivateDialog = useCallback(() => {
    setIsOpenDeactivateDialog(false);
    setItemToDeactivate(null);
  }, []);

  const handleDisableOneLink = useCallback(
    ({ uid }: IOneLink) => {
      return new Promise<string>((resolve, reject) => {
        setDisabledLoaderIds((prev) => (prev.includes(uid) ? prev : [...prev, uid]));
        dispatch(disableOneLinkRequest({ uid, resolve, reject }));
      })
        .then((id: string) => {
          dispatch(optimisticDeactivateOneLink(uid));
          setDisabledLoaderIds((prev) => prev.filter((i) => i !== id));
        })
        .catch(handleApiError('Cannot disable One Link Send'));
    },
    [dispatch],
  );

  const handlePaginationChange = useCallback(
    ({ page, pageSize, setPage, setPageSize }: IPaginatedListChangeEvent) => {
      const pageSizeToUse = pageSize || currentPageSize;

      fetchOneLinkSends({
        page,
        page_size: pageSizeToUse,
      });
      setPage(page);
      setPageSize?.(pageSizeToUse);
      setCurrentPage(page);
      setCurrentPageSize(pageSizeToUse);
    },
    [currentPageSize, fetchOneLinkSends],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize);
      setCurrentPage(INITIAL_PAGE);
      fetchOneLinkSends({
        page: INITIAL_PAGE,
        page_size: newPageSize,
      });
    },
    [fetchOneLinkSends],
  );

  useEffect(() => {
    if (!reasons || !reasons.length) {
      dispatch(fetchBusinessReasonsRequest());
    }

    dispatch(fetchUsersRequest());
  }, [reasons, dispatch]);

  useEffect(() => {
    if (typeof setHeader === 'function') {
      setHeader({ title: 'One link manager', action: () => history.push(routes.dashboard) });
    }
  }, [setHeader, history]);

  // resets the page to 1 when filters change (skip initial render — useFilters handles the first fetch)
  const isFiltersInitialRender = useRef(true);

  useEffect(() => {
    if (isFiltersInitialRender.current) {
      isFiltersInitialRender.current = false;
      return;
    }

    setCurrentPage(INITIAL_PAGE);
    fetchOneLinkSends({
      page: INITIAL_PAGE,
      page_size: currentPageSize,
    });

    paginatedListRef.current?.resetPageCount();
  }, [filters, fetchOneLinkSends]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexFlow: 'column' }}>
      <PageHeader>{isAdminTypeUser && Filters}</PageHeader>
      {activeView === ViewTypesEnum.List ? (
        <OneLinkManagerListView
          onChange={handlePaginationChange}
          items={filteredOneLinks}
          totalCount={oneLinkTotalCount}
          isOneLinksFetching={isOneLinksFetching}
          filters={filters}
          onOpenDeactivateDialog={handleOpenDeactivateDialog}
          ref={filtersRef}
          page={currentPage}
          pageSize={currentPageSize}
        />
      ) : (
        <OneLinkManagerGridView
          totalCount={oneLinkTotalCount}
          filteredOneLinks={filteredOneLinks}
          onChange={handlePaginationChange}
          isFinalOneLink={isFinalOneLink}
          isOneLinksFetching={isOneLinksFetching}
          onOpenDeactivateDialog={handleOpenDeactivateDialog}
          disabledLoaderIds={disabledLoaderIds}
          filtersRef={filtersRef}
          page={currentPage}
          pageSize={currentPageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
      <DeactivateOneLinkModal
        isOpenDeactivateDialog={isOpenDeactivateDialog}
        handleCloseDeactivateDialog={handleCloseDeactivateDialog}
        selectedItem={itemToDeactivate}
        handleDisableOneLink={handleDisableOneLink}
      />
    </Box>
  );
};

OneLinksManagerContainer.whyDidYouRender = false;

export default OneLinksManagerContainer;
